/**
 * Button Component
 */

import React, { forwardRef } from 'react';
import { useTheme } from 'styled-components';
import COLORS from '../../style/universal.color';
import Icon from '../Icon/icon';
import { ButtonColors, IndividualButtonColors, retrieveTeamColorMap } from './button.colors';
import { Label, StyledButton } from './button.style';
import {
  ButtonProps,
  ButtonShapes,
  ButtonSizes,
  buttonSizeMap,
  ButtonStyles,
  ButtonTypes,
  IndividualButtonSizes,
  ButtonRef,
} from './button.types';

const { linkBlue } = COLORS;
const DEFAULT_DATA_ATTR = { 'data-testid': 'button' };

// Returns a simple string for qualifiying button types
const retrieveStyleButtonType = (style: ButtonStyles): string => {
  switch (style) {
    case ButtonStyles.DEFAULT:
      return 'default';
    case ButtonStyles.BLACK:
      return 'black';
    case ButtonStyles.GREY:
      return 'grey';
    case ButtonStyles.TICKET_LINK_GREEN:
      return 'ticketGreen';
    case ButtonStyles.MLB_TV_ORANGE:
      return 'mlbTvOrange';
    default:
      return 'linkBlue';
  }
};

const Button = forwardRef<ButtonRef, ButtonProps>((props, ref) => {
  const {
    active = false,
    additionalProps = {},
    ariaLabel,
    ariaPressed = false,
    children,
    className,
    dataAttr = {},
    disabled = false,
    handlers = {},
    hasHover = true,
    hoverSpeed,
    iconHoverColor,
    iconLeft = true,
    iconName,
    iconProps = {},
    iconSize,
    id,
    label,
    role = 'button',
    shape = ButtonShapes.SOLID,
    size = ButtonSizes.SMALL,
    styleName = ButtonStyles.LINK_BLUE,
    type = ButtonTypes.BUTTON,
    value,
  } = props;

  const theme = useTheme();
  let { iconColor } = props;
  const { dark = false, teamColor1 } = theme;

  let hSpeed: number;
  if (hoverSpeed !== undefined) {
    hSpeed = hoverSpeed;
  } else if (theme.hoverSpeed !== undefined) {
    hSpeed = theme.hoverSpeed;
  } else {
    hSpeed = 0.2;
  }

  // Establish the specific version of the button
  const themeType: string = dark ? 'dark' : 'light';
  const hollowMode: string = [ButtonShapes.SOLID, ButtonShapes.PILL_SOLID].includes(shape) ? 'solid' : 'hollow';
  const buttonType: string = disabled ? 'disabled' : retrieveStyleButtonType(styleName);
  const borderless: boolean = shape === ButtonShapes.HOLLOW_BORDERLESS;
  const colorMap: IndividualButtonColors =
    styleName === ButtonStyles.TEAM
      ? retrieveTeamColorMap(teamColor1 || linkBlue, hollowMode, themeType)
      : // eslint-disable-next-line @typescript-eslint/ban-ts-comment
        // @ts-ignore
        ButtonColors[themeType][hollowMode][buttonType];

  // Retrieve the values from the color map
  const { backgroundColor, borderColor, color, hoverBackgroundColor, hoverBorderColor, hoverColor } = colorMap;

  // Establish if only an icon was provided which will tailor the padding in the button
  const onlyIcon: boolean = !(label || children);

  // Retrieve a map of sizes required by the button (height, padding, etc.);
  const sizeStyles: IndividualButtonSizes = buttonSizeMap[size];

  iconColor = iconColor || color;

  // Call the handler when the event is triggered
  const invokeHandler = (t: string, e?: Event) => {
    handlers[t](e, value);
  };

  const combinedDataAttr = { ...DEFAULT_DATA_ATTR, ...dataAttr };
  // Construct an object of props which will contain all aria labels, the value, handlers, etc.
  const completeButtonProps: { [key: string]: unknown } = {
    'aria-label': ariaLabel || label,
    'aria-pressed': ariaPressed,
    className,
    disabled,
    ref,
    role,
    type,
    value,
    ...combinedDataAttr,
    ...additionalProps,
  };
  Object.keys(handlers).forEach((key: string) => {
    completeButtonProps[key] = (e: Event) => {
      if (!disabled) {
        invokeHandler(key, e);
      }
    };
  });

  const iconWidth = iconSize || sizeStyles.iconSize;

  return (
    <StyledButton
      sizeStyles={sizeStyles}
      hSpeed={hSpeed}
      shape={shape}
      backgroundColor={backgroundColor}
      borderless={borderless}
      borderColor={borderColor}
      onlyIcon={onlyIcon}
      color={color}
      iconColor={iconColor}
      icon={!!iconName}
      iconLeft={iconLeft}
      disabled={disabled}
      hasHover={hasHover}
      hoverBackgroundColor={hoverBackgroundColor}
      hoverColor={hoverColor}
      hoverBorderColor={hoverBorderColor}
      iconHoverColor={iconHoverColor}
      styleName={styleName}
      id={id}
      active={active}
      {...completeButtonProps}
    >
      {iconLeft && iconName && <Icon width={iconWidth} name={iconName} fill={iconColor} {...iconProps} />}
      <Label>{label || children}</Label>
      {!iconLeft && iconName && <Icon width={iconWidth} name={iconName} fill={iconColor} {...iconProps} />}
    </StyledButton>
  );
});

export default Button;
